// 051 — Historia Clínica Materna Perinatal (Fase 4, Tanda 1b, ver PLAN.md
// "FASE 4 — spec completa"). Cierra la página 1: Parto/Aborto, Postparto,
// Morbilidad e Intervenciones — sigue el mismo criterio de
// `bloques051IdentificacionDatos.ts` (Tanda 1a): todo `Par[]` de respaldo en
// el PDF, sin recurso FHIR nuevo (nada acá tiene un consumidor clínico
// discreto que justifique crear uno — a diferencia de gestas/partos/FUM/
// altura uterina de la Tanda 1a, que sí lo tenían por precedente ya
// existente en el extractor legado de Patrón A).
//
// Parte (a) de la reestructuración (Ola 2): el bloque Parto/Aborto se rehízo
// contra el papel (form. 051, mayo/2016, pág. 1). Este formulario no relee lo
// ya guardado (solo escribe el `Par[]` firmado), así que los cambios de modelo
// no requieren migrar nada: lo firmado antes conserva sus textos en el PDF.
import type { Par } from './index';
import { conCodigoCie, leerSiNo, textoSiNo, type SiNoValor } from './camposSimplesDatos';
import type { DiagnosticoCie10Valor } from './SelectorDiagnosticoCie10';
import { estadoInicialPostparto051, paresDePostparto051, type EstadoPostparto051 } from './bloques051ControlesDatos';
import type { Traducir } from './tecnicaAnestesicaDatos';

/** 'Sí' / 'No' / '' — el papel imprime NO y SI: un "No" contestado debe llegar al PDF firmado. */
function siNo(t: Traducir, v: SiNoValor): string {
  return textoSiNo(v, { si: t('formulario.si'), no: t('formulario.no') });
}

function opcion(t: Traducir, prefijo: string, valor: string): string {
  return valor ? t(`${prefijo}.${valor}`) : '';
}

// ---------- Parto / Aborto ----------

export const TIPO_EVENTO_OPCIONES = ['parto', 'aborto'] as const;
export type TipoEvento = (typeof TIPO_EVENTO_OPCIONES)[number];

/** Corticoides antenatales: completo / incompleto / ninguna / n/a (papel: "completo, incompl., ninguna, n/a" + semana de inicio). */
export const CORTICOIDES_OPCIONES = ['completo', 'incompleto', 'ninguna', 'noAplica'] as const;
export type CorticoidesAntenatales = (typeof CORTICOIDES_OPCIONES)[number];

/** Uteroinhibidores: si / no / n/a + semana de inicio. */
export const UTEROINHIBIDORES_OPCIONES = ['si', 'no', 'noAplica'] as const;
export type Uteroinhibidores = (typeof UTEROINHIBIDORES_OPCIONES)[number];

export const INICIO_PARTO_OPCIONES = ['espontaneo', 'inducido', 'cesarea'] as const;
export type InicioParto = (typeof INICIO_PARTO_OPCIONES)[number];

export const EG_METODO_OPCIONES = ['fum', 'eco'] as const;
export type EgMetodo = (typeof EG_METODO_OPCIONES)[number];

export const PRESENTACION_OPCIONES = ['cefalica', 'pelviana', 'transversa'] as const;
export type PresentacionParto = (typeof PRESENTACION_OPCIONES)[number];

export const ACOMPANANTE_OPCIONES = ['pareja', 'familiar', 'partera', 'otro', 'ninguno'] as const;
export type Acompanante = (typeof ACOMPANANTE_OPCIONES)[number];

/** Cada práctica se contesta con no / si (el papel trae las dos filas por columna). */
export const PRACTICAS_INTERCULTURALES_OPCIONES = ['vestimenta', 'entregaPlacenta', 'ingestaLiquidos', 'otras'] as const;

export const POSICION_PARTO_OPCIONES = ['arrodillada', 'sentada', 'cuclillas', 'acostadaDeLado', 'deIPie', 'litotomica'] as const;
export type PosicionParto = (typeof POSICION_PARTO_OPCIONES)[number];

export const NACIMIENTO_ESTABLECIMIENTO_OPCIONES = ['establecimientoSalud', 'casa', 'otro'] as const;
export type NacimientoEstablecimiento = (typeof NACIMIENTO_ESTABLECIMIENTO_OPCIONES)[number];

/** Nacimiento: VIVO, o MUERTO en (Parto / Anteparto / Se ignora). */
export const NACIMIENTO_CONDICION_OPCIONES = ['vivo', 'muertoParto', 'muertoAnteparto', 'muertoSeIgnora'] as const;
export type NacimientoCondicion = (typeof NACIMIENTO_CONDICION_OPCIONES)[number];

export const TERMINACION_PARTO_OPCIONES = ['espontaneo', 'forceps', 'vacuum', 'cesarea', 'otra'] as const;
export type TerminacionParto = (typeof TERMINACION_PARTO_OPCIONES)[number];

export const DESGARRO_GRADO_OPCIONES = ['1', '2', '3', '4'] as const;
export type DesgarroGrado = (typeof DESGARRO_GRADO_OPCIONES)[number];

/**
 * "Recibió": cada ítem es no / si en el papel. Analgesia es una sola casilla; anestesia se separa en local, regional
 * y general; el sulfato de Mg se registra aparte para preeclampsia y para eclampsia.
 */
export const RECIBIO_PARTO_OPCIONES = [
  'inductoconduccionTdp',
  'oxitocicosTdp',
  'antibiotico',
  'analgesia',
  'anestesiaLocal',
  'anestesiaRegional',
  'anestesiaGeneral',
  'transfusion',
  'sulfatoMgPreeclampsia',
  'sulfatoMgEclampsia',
] as const;

export interface EstadoPartoAborto051 {
  readonly tipoEvento: TipoEvento | '';
  readonly fechaIngreso: string;
  readonly consultasPrenatales: string;
  readonly hospitalizacionEmbarazo: SiNoValor;
  readonly hospitalizacionEmbarazoDias: string;
  readonly carne: SiNoValor;
  readonly corticoidesAntenatales: CorticoidesAntenatales | '';
  readonly corticoidesSemanaInicio: string;
  readonly uteroinhibidores: Uteroinhibidores | '';
  readonly uteroinhibidoresSemanaInicio: string;
  readonly inicio: InicioParto | '';
  readonly rupturaMembranasAnteparto: SiNoValor;
  readonly rupturaMembranasFecha: string;
  readonly rupturaMembranasHora: string;
  readonly rupturaMembranasTemperatura: string;
  readonly rupturaMembranasMenor37Semanas: SiNoValor;
  readonly rupturaMembranasMayorIgual18h: SiNoValor;
  readonly egSemanas: string;
  readonly egDias: string;
  readonly egMetodo: EgMetodo | '';
  readonly presentacionSituacion: PresentacionParto | '';
  readonly pesoProducto: string;
  readonly pesoProductoNoAplica: boolean;
  readonly tamanoFetalAcordeEg: SiNoValor;
  readonly acompananteTdp: Acompanante | '';
  readonly acompananteParto: Acompanante | '';
  readonly practicasInterculturales: Readonly<Record<string, SiNoValor>>;
  readonly practicasInterculturalesCual: string;
  readonly posicionParto: PosicionParto | '';
  readonly nacimientoEstablecimiento: NacimientoEstablecimiento | '';
  readonly nacimientoFecha: string;
  readonly nacimientoHora: string;
  readonly nacimientoCondicion: NacimientoCondicion | '';
  readonly episiotomia: SiNoValor;
  readonly desgarro: SiNoValor;
  readonly desgarroGrado: DesgarroGrado | '';
  readonly manejoActivo: SiNoValor;
  readonly multiple: SiNoValor;
  readonly multipleOrden: string;
  readonly terminacion: TerminacionParto | '';
  readonly indicacionPrincipal: string;
  readonly cieInduccion: DiagnosticoCie10Valor;
  readonly cieOperatoria: DiagnosticoCie10Valor;
  readonly recibio: Readonly<Record<string, SiNoValor>>;
  readonly placentaCompleta: SiNoValor;
  readonly placentaRetenida: SiNoValor;
  readonly ligaduraCordonAlCesarPulsaciones: SiNoValor;
  readonly medicacion: SiNoValor;
  readonly medicacionCual: string;
}

export function estadoInicialPartoAborto051(): EstadoPartoAborto051 {
  return {
    tipoEvento: '',
    fechaIngreso: '',
    consultasPrenatales: '',
    hospitalizacionEmbarazo: '',
    hospitalizacionEmbarazoDias: '',
    carne: '',
    corticoidesAntenatales: '',
    corticoidesSemanaInicio: '',
    uteroinhibidores: '',
    uteroinhibidoresSemanaInicio: '',
    inicio: '',
    rupturaMembranasAnteparto: '',
    rupturaMembranasFecha: '',
    rupturaMembranasHora: '',
    rupturaMembranasTemperatura: '',
    rupturaMembranasMenor37Semanas: '',
    rupturaMembranasMayorIgual18h: '',
    egSemanas: '',
    egDias: '',
    egMetodo: '',
    presentacionSituacion: '',
    pesoProducto: '',
    pesoProductoNoAplica: false,
    tamanoFetalAcordeEg: '',
    acompananteTdp: '',
    acompananteParto: '',
    practicasInterculturales: {},
    practicasInterculturalesCual: '',
    posicionParto: '',
    nacimientoEstablecimiento: '',
    nacimientoFecha: '',
    nacimientoHora: '',
    nacimientoCondicion: '',
    episiotomia: '',
    desgarro: '',
    desgarroGrado: '',
    manejoActivo: '',
    multiple: '',
    multipleOrden: '',
    terminacion: '',
    indicacionPrincipal: '',
    cieInduccion: { texto: '', cie10: null },
    cieOperatoria: { texto: '', cie10: null },
    recibio: {},
    placentaCompleta: '',
    placentaRetenida: '',
    ligaduraCordonAlCesarPulsaciones: '',
    medicacion: '',
    medicacionCual: '',
  };
}

/** Grilla no/si por clave (prácticas interculturales, "Recibió"): una fila por opción con su Sí/No. */
function paresDeGrillaSiNo(t: Traducir, prefijoI18n: string, opciones: readonly string[], valores: Readonly<Record<string, SiNoValor>>): Par[] {
  return opciones.map((c) => ({ pregunta: `${t(`${prefijoI18n}.titulo`)} — ${t(`${prefijoI18n}.opciones.${c}`)}`, respuesta: siNo(t, valores[c] ?? '') }));
}

/** Un dato que depende de una respuesta (grado del desgarro, días de hospitalización…) no se imprime si esa respuesta quedó en "No" / "Ninguna" / "n/a": evita firmar un valor huérfano tras corregir. */
function paresDePartoAborto051(t: Traducir, e: EstadoPartoAborto051): Par[] {
  const p = 'formularios.f051.partoAborto';
  const pesoRespuesta = e.pesoProducto.trim() || (e.pesoProductoNoAplica ? t(`${p}.noAplica`) : '');
  return [
    { pregunta: t(`${p}.tipoEvento`), respuesta: opcion(t, `${p}.tipoEventoOpciones`, e.tipoEvento) },
    { pregunta: t(`${p}.fechaIngreso`), respuesta: e.fechaIngreso.trim() },
    { pregunta: t(`${p}.consultasPrenatales`), respuesta: e.consultasPrenatales.trim() },
    { pregunta: t(`${p}.hospitalizacionEmbarazo`), respuesta: siNo(t, e.hospitalizacionEmbarazo) },
    { pregunta: t(`${p}.hospitalizacionEmbarazoDias`), respuesta: e.hospitalizacionEmbarazo === 'no' ? '' : e.hospitalizacionEmbarazoDias.trim() },
    { pregunta: t(`${p}.carne`), respuesta: siNo(t, e.carne) },
    { pregunta: t(`${p}.corticoidesAntenatales`), respuesta: opcion(t, `${p}.corticoidesAntenatalesOpciones`, e.corticoidesAntenatales) },
    { pregunta: t(`${p}.corticoidesSemanaInicio`), respuesta: e.corticoidesAntenatales === 'ninguna' || e.corticoidesAntenatales === 'noAplica' ? '' : e.corticoidesSemanaInicio.trim() },
    { pregunta: t(`${p}.uteroinhibidores`), respuesta: opcion(t, `${p}.uteroinhibidoresOpciones`, e.uteroinhibidores) },
    { pregunta: t(`${p}.uteroinhibidoresSemanaInicio`), respuesta: e.uteroinhibidores === 'no' || e.uteroinhibidores === 'noAplica' ? '' : e.uteroinhibidoresSemanaInicio.trim() },
    { pregunta: t(`${p}.inicio`), respuesta: opcion(t, `${p}.inicioOpciones`, e.inicio) },
    { pregunta: t(`${p}.rupturaMembranasAnteparto`), respuesta: siNo(t, e.rupturaMembranasAnteparto) },
    { pregunta: t(`${p}.rupturaMembranasFecha`), respuesta: e.rupturaMembranasFecha.trim() },
    { pregunta: t(`${p}.rupturaMembranasHora`), respuesta: e.rupturaMembranasHora.trim() },
    { pregunta: t(`${p}.rupturaMembranasTemperatura`), respuesta: e.rupturaMembranasTemperatura.trim() },
    { pregunta: t(`${p}.rupturaMembranasMenor37Semanas`), respuesta: siNo(t, e.rupturaMembranasMenor37Semanas) },
    { pregunta: t(`${p}.rupturaMembranasMayorIgual18h`), respuesta: siNo(t, e.rupturaMembranasMayorIgual18h) },
    { pregunta: t(`${p}.egSemanas`), respuesta: e.egSemanas.trim() },
    { pregunta: t(`${p}.egDias`), respuesta: e.egDias.trim() },
    { pregunta: t(`${p}.egMetodo`), respuesta: opcion(t, `${p}.egMetodoOpciones`, e.egMetodo) },
    { pregunta: t(`${p}.presentacionSituacion`), respuesta: opcion(t, `${p}.presentacionSituacionOpciones`, e.presentacionSituacion) },
    { pregunta: t(`${p}.pesoProducto`), respuesta: pesoRespuesta },
    { pregunta: t(`${p}.tamanoFetalAcordeEg`), respuesta: siNo(t, e.tamanoFetalAcordeEg) },
    { pregunta: t(`${p}.acompananteTdp`), respuesta: opcion(t, `${p}.acompananteOpciones`, e.acompananteTdp) },
    { pregunta: t(`${p}.acompananteParto`), respuesta: opcion(t, `${p}.acompananteOpciones`, e.acompananteParto) },
    ...paresDeGrillaSiNo(t, `${p}.practicasInterculturales`, PRACTICAS_INTERCULTURALES_OPCIONES, e.practicasInterculturales),
    { pregunta: t(`${p}.practicasInterculturalesCual`), respuesta: e.practicasInterculturalesCual.trim() },
    { pregunta: t(`${p}.posicionParto`), respuesta: opcion(t, `${p}.posicionPartoOpciones`, e.posicionParto) },
    { pregunta: t(`${p}.nacimientoEstablecimiento`), respuesta: opcion(t, `${p}.nacimientoEstablecimientoOpciones`, e.nacimientoEstablecimiento) },
    { pregunta: t(`${p}.nacimientoFecha`), respuesta: e.nacimientoFecha.trim() },
    { pregunta: t(`${p}.nacimientoHora`), respuesta: e.nacimientoHora.trim() },
    { pregunta: t(`${p}.nacimientoCondicion`), respuesta: opcion(t, `${p}.nacimientoCondicionOpciones`, e.nacimientoCondicion) },
    { pregunta: t(`${p}.episiotomia`), respuesta: siNo(t, e.episiotomia) },
    { pregunta: t(`${p}.desgarro`), respuesta: siNo(t, e.desgarro) },
    { pregunta: t(`${p}.desgarroGrado`), respuesta: e.desgarro === 'no' ? '' : e.desgarroGrado },
    { pregunta: t(`${p}.manejoActivo`), respuesta: siNo(t, e.manejoActivo) },
    { pregunta: t(`${p}.multiple`), respuesta: siNo(t, e.multiple) },
    { pregunta: t(`${p}.multipleOrden`), respuesta: e.multiple === 'no' ? '' : e.multipleOrden.trim() },
    { pregunta: t(`${p}.terminacion`), respuesta: opcion(t, `${p}.terminacionOpciones`, e.terminacion) },
    { pregunta: t(`${p}.indicacionPrincipal`), respuesta: e.indicacionPrincipal.trim() },
    { pregunta: t(`${p}.cieInduccion`), respuesta: conCodigoCie(e.cieInduccion) },
    { pregunta: t(`${p}.cieOperatoria`), respuesta: conCodigoCie(e.cieOperatoria) },
    ...paresDeGrillaSiNo(t, `${p}.recibio`, RECIBIO_PARTO_OPCIONES, e.recibio),
    { pregunta: t(`${p}.placentaCompleta`), respuesta: siNo(t, e.placentaCompleta) },
    { pregunta: t(`${p}.placentaRetenida`), respuesta: siNo(t, e.placentaRetenida) },
    { pregunta: t(`${p}.ligaduraCordonAlCesarPulsaciones`), respuesta: siNo(t, e.ligaduraCordonAlCesarPulsaciones) },
    { pregunta: t(`${p}.medicacion`), respuesta: siNo(t, e.medicacion) },
    { pregunta: t(`${p}.medicacionCual`), respuesta: e.medicacionCual.trim() },
  ];
}

// ---------- Morbilidad ----------
//
// Parte (e): cada ítem del papel es un par no/si → tri-estado (`SiNoValor`); el "No" llega al `Par[]` firmado. Cada grupo trae su
// propio no/si de cabecera ("sin/con trastornos hipertensivos"…): si la cabecera queda en "No", sus ítems no se imprimen (evita firmar
// un "Sí" huérfano tras corregir). Hemorragia trae un no/si por trimestre; los tipos son independientes del trimestre (hemorragia postparto con los tres en "No" es el caso típico).
// Lo guardado con el modelo viejo (casillas booleanas, un solo trimestre) se lee con `leerMorbilidad051`, sin migrar.

export const TRASTORNOS_HIPERTENSIVOS_OPCIONES = ['eclampsia', 'help', 'transitoria', 'previaConProteinuriaSobreagregada', 'previa', 'preeclampsia'] as const;

export const INFECCIONES_051_OPCIONES = [
  'sirs',
  'sepsis',
  'endometritis',
  'infeccionOvular',
  'infeccionGenital',
  'bacteriuriaAsintomatica',
  'pielonefritis',
  'neumonia',
  'mastitis',
  'gonorrea',
  'parasitosis',
  'infeccionHeridaCesarea',
  'infeccionEpisiorrafia',
  'otraInfeccion',
] as const;

export const HEMORRAGIA_TRIMESTRE_OPCIONES = ['1er', '2do', '3er'] as const;
export type HemorragiaTrimestre = (typeof HEMORRAGIA_TRIMESTRE_OPCIONES)[number];

export const HEMORRAGIA_TIPOS_OPCIONES = [
  'postAborto',
  'molaHidatiforme',
  'embarazoEctopico',
  'placentaPrevia',
  'acretismoPlacentario',
  'dppni',
  'roturaUterina',
  'hemorragiaPostparto',
  'atoniaUterina',
  'desgarros',
  'restos',
  'defectoCoagulacion',
] as const;

export const TRASTORNOS_METABOLICOS_DIABETES_OPCIONES = ['toleranciaOralGlucosaAnormal', 'dmInsulinoDependientePrevia', 'dmNoInsulinoDependientePrevia', 'dmGestacional'] as const;

export const TRASTORNOS_TIROIDEOS_OPCIONES = ['hipotiroidismo', 'hipertiroidismo', 'crisisTiroidea', 'otro'] as const;

export const OTROS_TRASTORNOS_051_OPCIONES = [
  'hiperemesisGravidica',
  'trombosisVenosaProfunda',
  'tromboembolismoPulmonar',
  'emboliaLiquidoAmniotico',
  'cardiopatia',
  'valvulopatia',
  'convulsiones',
  'oliguria',
  'anemiaFerropriva',
  'anemiaFalciforme',
  'enfermedadRenal',
  'neoplasiaMaligna',
  'trastornoPsiquiatrico',
  'alteracionEstadoConciencia',
] as const;

export const COMPLICACIONES_OBSTETRICAS_OPCIONES = ['partoObstruido', 'roturaProlongadaMembranas', 'oligoamnios', 'polihidramnio', 'sufrimientoFetalAgudo', 'restriccionCrecimientoIntrauterino', 'otros'] as const;

/** Inmunoglobulina anti Rh D: no / si / n/c (no corresponde). */
export const INMUNOGLOBULINA_ANTI_RH_D_OPCIONES = ['si', 'no', 'nc'] as const;
export type InmunoglobulinaAntiRhD = (typeof INMUNOGLOBULINA_ANTI_RH_D_OPCIONES)[number];

type MapaSiNo = Readonly<Record<string, SiNoValor>>;

export interface EstadoMorbilidad051 {
  readonly trastornosHipertensivosPresente: SiNoValor;
  readonly trastornosHipertensivos: MapaSiNo;
  readonly infeccionesPresente: SiNoValor;
  readonly infecciones: MapaSiNo;
  readonly otraInfeccionCual: string;
  /** Un no/si por trimestre (papel: "1 TRIM. no/si, 2 TRIM. no/si, 3 TRIM. no/si"). */
  readonly hemorragiaTrimestres: MapaSiNo;
  readonly hemorragiaTipos: MapaSiNo;
  readonly inmunoglobulinaAntiRhD: InmunoglobulinaAntiRhD | '';
  readonly trastornosMetabolicosPresente: SiNoValor;
  readonly diabetesPresente: SiNoValor;
  readonly trastornosMetabolicosDiabetes: MapaSiNo;
  readonly tiroideosPresente: SiNoValor;
  readonly trastornosTiroideos: MapaSiNo;
  readonly otroTrastornoMetabolicoCual: string;
  readonly otrosTrastornosPresente: SiNoValor;
  readonly otrosTrastornos: MapaSiNo;
  readonly complicacionesPresente: SiNoValor;
  readonly complicacionesObstetricas: MapaSiNo;
  readonly complicacionesOtrosCual: string;
}

export function estadoInicialMorbilidad051(): EstadoMorbilidad051 {
  return {
    trastornosHipertensivosPresente: '',
    trastornosHipertensivos: {},
    infeccionesPresente: '',
    infecciones: {},
    otraInfeccionCual: '',
    hemorragiaTrimestres: {},
    hemorragiaTipos: {},
    inmunoglobulinaAntiRhD: '',
    trastornosMetabolicosPresente: '',
    diabetesPresente: '',
    trastornosMetabolicosDiabetes: {},
    tiroideosPresente: '',
    trastornosTiroideos: {},
    otroTrastornoMetabolicoCual: '',
    otrosTrastornosPresente: '',
    otrosTrastornos: {},
    complicacionesPresente: '',
    complicacionesObstetricas: {},
    complicacionesOtrosCual: '',
  };
}

function esObjeto(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function textoLibre(v: unknown): string {
  return typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '';
}

/** Mapa clave → SiNoValor desde el modelo nuevo (`'si'|'no'`) o el viejo (`true`): el modelo viejo no distinguía el No, queda sin responder. */
function leerMapaSiNo(v: unknown): Record<string, SiNoValor> {
  if (!esObjeto(v)) return {};
  return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, leerSiNo(x)] as const).filter(([, x]) => x !== ''));
}

/** Lee lo guardado de Morbilidad con el modelo nuevo o el viejo (casillas booleanas, un solo trimestre, DPPNI y rotura uterina juntas). */
export function leerMorbilidad051(valor: unknown): EstadoMorbilidad051 {
  const base = estadoInicialMorbilidad051();
  if (!esObjeto(valor)) return base;
  const tipos = leerMapaSiNo(valor.hemorragiaTipos);
  if (tipos.dppniRoturaUterina) {
    // Modelo viejo: una sola casilla "DPPNI / Rotura uterina"; no se puede saber cuál era: se conserva como DPPNI (primera del papel).
    tipos.dppni = tipos.dppni ?? tipos.dppniRoturaUterina;
    delete tipos.dppniRoturaUterina;
  }
  const trimestres = leerMapaSiNo(valor.hemorragiaTrimestres);
  if (typeof valor.hemorragiaTrimestre === 'string' && (HEMORRAGIA_TRIMESTRE_OPCIONES as readonly string[]).includes(valor.hemorragiaTrimestre)) trimestres[valor.hemorragiaTrimestre] = 'si';
  const rh = valor.inmunoglobulinaAntiRhD;
  return {
    ...base,
    trastornosHipertensivosPresente: leerSiNo(valor.trastornosHipertensivosPresente),
    trastornosHipertensivos: leerMapaSiNo(valor.trastornosHipertensivos),
    infeccionesPresente: leerSiNo(valor.infeccionesPresente),
    infecciones: leerMapaSiNo(valor.infecciones),
    otraInfeccionCual: textoLibre(valor.otraInfeccionCual),
    hemorragiaTrimestres: trimestres,
    hemorragiaTipos: tipos,
    inmunoglobulinaAntiRhD: rh === 'si' || rh === 'no' || rh === 'nc' ? rh : rh === true ? 'si' : '',
    trastornosMetabolicosPresente: leerSiNo(valor.trastornosMetabolicosPresente),
    diabetesPresente: leerSiNo(valor.diabetesPresente),
    trastornosMetabolicosDiabetes: leerMapaSiNo(valor.trastornosMetabolicosDiabetes),
    tiroideosPresente: leerSiNo(valor.tiroideosPresente),
    trastornosTiroideos: leerMapaSiNo(valor.trastornosTiroideos),
    otroTrastornoMetabolicoCual: textoLibre(valor.otroTrastornoMetabolicoCual),
    otrosTrastornosPresente: leerSiNo(valor.otrosTrastornosPresente),
    otrosTrastornos: leerMapaSiNo(valor.otrosTrastornos),
    complicacionesPresente: leerSiNo(valor.complicacionesPresente),
    complicacionesObstetricas: leerMapaSiNo(valor.complicacionesObstetricas),
    complicacionesOtrosCual: textoLibre(valor.complicacionesOtrosCual),
  };
}

/** Cabecera no/si de un grupo + sus ítems no/si. Con la cabecera en "No" los ítems no se imprimen. */
function paresDeGrupoMorbilidad(t: Traducir, prefijoI18n: string, opciones: readonly string[], presente: SiNoValor, valores: MapaSiNo): Par[] {
  const titulo = t(`${prefijoI18n}.titulo`);
  const pares: Par[] = [{ pregunta: titulo, respuesta: siNo(t, presente) }];
  if (presente === 'no') return pares;
  for (const c of opciones) pares.push({ pregunta: `${titulo} — ${t(`${prefijoI18n}.opciones.${c}`)}`, respuesta: siNo(t, valores[c] ?? '') });
  return pares;
}

function paresDeMorbilidad051(t: Traducir, e: EstadoMorbilidad051): Par[] {
  const m = 'formularios.f051.morbilidad';
  const pares: Par[] = [];
  pares.push(...paresDeGrupoMorbilidad(t, `${m}.trastornosHipertensivos`, TRASTORNOS_HIPERTENSIVOS_OPCIONES, e.trastornosHipertensivosPresente, e.trastornosHipertensivos));

  pares.push(...paresDeGrupoMorbilidad(t, `${m}.infecciones`, INFECCIONES_051_OPCIONES, e.infeccionesPresente, e.infecciones));
  if (e.infeccionesPresente !== 'no' && e.infecciones.otraInfeccion === 'si') pares.push({ pregunta: t(`${m}.otraInfeccionCual`), respuesta: e.otraInfeccionCual.trim() });

  for (const tr of HEMORRAGIA_TRIMESTRE_OPCIONES) {
    pares.push({ pregunta: `${t(`${m}.hemorragiaTrimestre`)} — ${t(`${m}.hemorragiaTrimestreOpciones.${tr}`)}`, respuesta: siNo(t, e.hemorragiaTrimestres[tr] ?? '') });
  }
  // Los tipos NO cuelgan de los trimestres: la columna derecha del papel (rotura uterina, hemorragia postparto, atonía, desgarros, restos,
  // defecto de coagulación) es del parto/puerperio, donde los tres trimestres quedan legítimamente en "No".
  for (const c of HEMORRAGIA_TIPOS_OPCIONES) pares.push({ pregunta: `${t(`${m}.hemorragiaTipos.titulo`)} — ${t(`${m}.hemorragiaTipos.opciones.${c}`)}`, respuesta: siNo(t, e.hemorragiaTipos[c] ?? '') });

  pares.push({ pregunta: t(`${m}.inmunoglobulinaAntiRhD`), respuesta: e.inmunoglobulinaAntiRhD ? t(`${m}.inmunoglobulinaAntiRhDOpciones.${e.inmunoglobulinaAntiRhD}`) : '' });

  pares.push({ pregunta: t(`${m}.trastornosMetabolicos`), respuesta: siNo(t, e.trastornosMetabolicosPresente) });
  if (e.trastornosMetabolicosPresente !== 'no') {
    pares.push(...paresDeGrupoMorbilidad(t, `${m}.trastornosMetabolicosDiabetes`, TRASTORNOS_METABOLICOS_DIABETES_OPCIONES, e.diabetesPresente, e.trastornosMetabolicosDiabetes));
    pares.push(...paresDeGrupoMorbilidad(t, `${m}.trastornosTiroideos`, TRASTORNOS_TIROIDEOS_OPCIONES, e.tiroideosPresente, e.trastornosTiroideos));
    if (e.tiroideosPresente !== 'no' && e.trastornosTiroideos.otro === 'si') pares.push({ pregunta: t(`${m}.otroTrastornoMetabolicoCual`), respuesta: e.otroTrastornoMetabolicoCual.trim() });
  }

  pares.push(...paresDeGrupoMorbilidad(t, `${m}.otrosTrastornos`, OTROS_TRASTORNOS_051_OPCIONES, e.otrosTrastornosPresente, e.otrosTrastornos));

  pares.push(...paresDeGrupoMorbilidad(t, `${m}.complicacionesObstetricas`, COMPLICACIONES_OBSTETRICAS_OPCIONES, e.complicacionesPresente, e.complicacionesObstetricas));
  if (e.complicacionesPresente !== 'no' && e.complicacionesObstetricas.otros === 'si') pares.push({ pregunta: t(`${m}.complicacionesOtrosCual`), respuesta: e.complicacionesOtrosCual.trim() });
  return pares;
}

// ---------- Intervenciones ----------
//
// El papel imprime tres círculos por ítem: si / no / s/d (sin dato). "s/d" es una respuesta distinta de "sin responder".

export type SiNoSdValor = 'si' | 'no' | 'sd' | '';

export function leerSiNoSd(v: unknown): SiNoSdValor {
  return v === 'sd' ? 'sd' : leerSiNo(v);
}

function textoSiNoSd(t: Traducir, v: SiNoSdValor): string {
  return v === 'sd' ? t('formularios.f051.nearMiss.opcionesLaboratorio.sd') : siNo(t, v);
}

export const INTERVENCIONES_051_OPCIONES = ['alumbramientoManual', 'uterotonicosTtoHemorragia', 'viaVenosaCentral', 'administracionHemoderivados', 'laparotomia', 'ingresoUci7Dias', 'usoAtbIv'] as const;

export const PROCEDIMIENTOS_HEMORRAGIA_OPCIONES = ['ligadurasHemostaticasArteriasUterinasHipogastricas', 'embolizaciones', 'trajeAntishockNoNeumatico', 'balonesHidrostaticos'] as const;

export interface EstadoIntervenciones051 {
  readonly intervenciones: Readonly<Record<string, SiNoSdValor>>;
  readonly uterotonicosCual: string;
  readonly hemoderivadosVolumenes: string;
  readonly ingresoUciDias: string;
  readonly usoAtbIvCual: string;
  readonly procedimientosHemorragia: Readonly<Record<string, SiNoSdValor>>;
}

export function estadoInicialIntervenciones051(): EstadoIntervenciones051 {
  return { intervenciones: {}, uterotonicosCual: '', hemoderivadosVolumenes: '', ingresoUciDias: '', usoAtbIvCual: '', procedimientosHemorragia: {} };
}

function leerMapaSiNoSd(v: unknown): Record<string, SiNoSdValor> {
  if (!esObjeto(v)) return {};
  return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, leerSiNoSd(x)] as const).filter(([, x]) => x !== ''));
}

/** Lee lo guardado de Intervenciones: modelo nuevo, o el viejo (una casilla booleana por intervención, sin "No" ni "s/d"). */
export function leerIntervenciones051(valor: unknown): EstadoIntervenciones051 {
  const base = estadoInicialIntervenciones051();
  if (!esObjeto(valor)) return base;
  const intervenciones = leerMapaSiNoSd(valor.intervenciones);
  for (const c of INTERVENCIONES_051_OPCIONES) {
    const viejo = leerSiNoSd(valor[c]);
    if (viejo !== '' && intervenciones[c] === undefined) intervenciones[c] = viejo;
  }
  return {
    intervenciones,
    uterotonicosCual: textoLibre(valor.uterotonicosCual),
    hemoderivadosVolumenes: textoLibre(valor.hemoderivadosVolumenes),
    ingresoUciDias: textoLibre(valor.ingresoUciDias),
    usoAtbIvCual: textoLibre(valor.usoAtbIvCual),
    procedimientosHemorragia: leerMapaSiNoSd(valor.procedimientosHemorragia),
  };
}

function paresDeIntervenciones051(t: Traducir, e: EstadoIntervenciones051): Par[] {
  const p = 'formularios.f051.intervenciones';
  // Dato que depende de una intervención: solo se imprime si esa intervención quedó en "Sí".
  const dependientes: Readonly<Record<string, { readonly clave: string; readonly valor: string }>> = {
    uterotonicosTtoHemorragia: { clave: 'uterotonicosCual', valor: e.uterotonicosCual },
    administracionHemoderivados: { clave: 'hemoderivadosVolumenes', valor: e.hemoderivadosVolumenes },
    ingresoUci7Dias: { clave: 'ingresoUciDias', valor: e.ingresoUciDias },
    usoAtbIv: { clave: 'usoAtbIvCual', valor: e.usoAtbIvCual },
  };
  const pares: Par[] = [];
  for (const c of INTERVENCIONES_051_OPCIONES) {
    const v = e.intervenciones[c] ?? '';
    pares.push({ pregunta: t(`${p}.${c}`), respuesta: textoSiNoSd(t, v) });
    const dep = dependientes[c];
    if (dep && v === 'si') pares.push({ pregunta: t(`${p}.${dep.clave}`), respuesta: dep.valor.trim() });
  }
  for (const c of PROCEDIMIENTOS_HEMORRAGIA_OPCIONES) {
    pares.push({ pregunta: `${t(`${p}.procedimientosHemorragia.titulo`)} — ${t(`${p}.procedimientosHemorragia.opciones.${c}`)}`, respuesta: textoSiNoSd(t, e.procedimientosHemorragia[c] ?? '') });
  }
  return pares;
}

// ---------- Estado combinado ----------

export interface EstadoParto051 {
  readonly partoAborto: EstadoPartoAborto051;
  readonly postparto: EstadoPostparto051;
  readonly morbilidad: EstadoMorbilidad051;
  readonly intervenciones: EstadoIntervenciones051;
}

export function estadoInicialParto051(): EstadoParto051 {
  return {
    partoAborto: estadoInicialPartoAborto051(),
    postparto: estadoInicialPostparto051(),
    morbilidad: estadoInicialMorbilidad051(),
    intervenciones: estadoInicialIntervenciones051(),
  };
}

/** Serializa Parto/Postparto/Morbilidad/Intervenciones a `Par[]`, listos para `onGuardado` — filtra respuestas vacías. */
export function paresDeParto051(t: Traducir, estado: EstadoParto051): Par[] {
  const pares: Par[] = [
    ...paresDePartoAborto051(t, estado.partoAborto),
    ...paresDePostparto051(t, estado.postparto),
    ...paresDeMorbilidad051(t, estado.morbilidad),
    ...paresDeIntervenciones051(t, estado.intervenciones),
  ];
  return pares.filter((p) => p.respuesta.trim() !== '');
}
